import { alpha, type Theme } from "@mui/material/styles";
import type { AppSx } from "../types";
import { buildFormLayoutStyles } from "../layout/formLayout";

export interface DateRangeHeaderComponentStyles {
  paper: AppSx;
  headerStack: (isVertical: boolean) => AppSx;
  filtersLabelStack: AppSx;
  actionsStack: (isVertical: boolean) => AppSx;
  rangeChip: (isVertical: boolean) => AppSx;
  refreshButton: AppSx;
  body: (isVertical: boolean) => AppSx;
  filtersGrid: (isVertical: boolean) => AppSx;
  presetField: (isVertical: boolean) => AppSx;
  customFromField: AppSx;
  customToField: AppSx;
  customApplyField: (isVertical: boolean) => AppSx;
  presetAutocomplete: AppSx;
  presetAutocompletePaper: AppSx;
  presetListOption: AppSx;
  presetListIcon: AppSx;
  presetInputIcon: AppSx;
  customApplyButton: (isVertical: boolean) => AppSx;
  yearMonthsLabel: AppSx;
  yearRow: AppSx;
  yearButton: (active: boolean) => AppSx;
  monthGrid: () => AppSx;
  monthTile: (active: boolean, compact: boolean, disabled?: boolean) => AppSx;
  monthTileIcon: (active: boolean) => AppSx;
  monthTileLabel: AppSx;
  divider: AppSx;
}

export function buildDateRangeHeaderComponentStyles(theme: Theme): DateRangeHeaderComponentStyles {
  const form = buildFormLayoutStyles(theme);
  return {
    paper: {
      mb: 0,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      overflow: "hidden",
      bgcolor: "background.paper",
      boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
    },
    headerStack: (isVertical) => ({
      px: isVertical ? 1.5 : 2,
      py: 1.25,
      bgcolor: alpha(
        theme.palette.primary.main,
        theme.palette.mode === "dark" ? 0.12 : 0.04
      ),
      borderBottom: "1px solid",
      borderColor: "divider",
    }),
    filtersLabelStack: { flexShrink: 0 },
    actionsStack: (isVertical) => ({
      flex: 1,
      minWidth: 0,
      justifyContent: isVertical ? "flex-start" : { lg: "flex-end" },
    }),
    rangeChip: (isVertical) => ({
      minWidth: 0,
      maxWidth: "100%",
      height: "auto",
      py: 0.5,
      alignSelf: isVertical ? "stretch" : undefined,
      "& .MuiChip-label": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: isVertical ? "normal" : "nowrap",
        display: "block",
      },
    }),
    refreshButton: { textTransform: "none", fontWeight: 600, px: 2, flexShrink: 0 },
    body: (isVertical) => ({ px: isVertical ? 1.25 : 1.5, pt: 1, pb: 1.25 }),
    filtersGrid: (isVertical) => ({
      display: "grid",
      gridTemplateColumns: isVertical
        ? "1fr"
        : {
            xs: "1fr",
            md: "minmax(160px, 200px) minmax(0, 1fr) minmax(0, 1fr) auto",
          },
      columnGap: 1,
      rowGap: 0.75,
      alignItems: "center",
    }),
    presetField: (isVertical) => ({
      width: "100%",
      minWidth: 0,
      gridColumn: isVertical ? "1 / -1" : { xs: "1 / -1", md: "auto" },
    }),
    customFromField: {
      minWidth: 0,
      gridColumn: { xs: "1 / -1", md: "auto" },
    },
    customToField: {
      minWidth: 0,
      gridColumn: { xs: "1 / -1", md: "auto" },
    },
    customApplyField: (isVertical) => ({
      minWidth: 0,
      gridColumn: isVertical ? "1 / -1" : { xs: "1 / -1", md: "auto" },
      justifySelf: isVertical ? "stretch" : { xs: "stretch", md: "end" },
    }),
    presetAutocomplete: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        minHeight: 44,
        bgcolor: "background.paper",
      },
    },
    presetAutocompletePaper: { mt: 0.5 },
    presetListOption: { gap: 1.25, py: 1, px: 1.5 },
    presetListIcon: { minWidth: 32 },
    presetInputIcon: { ml: 0.5, mr: 0.25 },
    customApplyButton: (isVertical) => ({
      ...form.formRowButton,
      textTransform: "none",
      fontWeight: 600,
      minWidth: { md: 88 },
      whiteSpace: "nowrap",
    }),
    yearMonthsLabel: {
      display: "block",
      mb: 0.5,
      mt: 1,
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    yearRow: {
      flexWrap: "wrap",
      mb: 0.25,
    },
    yearButton: (active) => ({
      minWidth: 72,
      fontWeight: active ? 700 : 600,
      textTransform: "none",
    }),
    monthGrid: () => ({
      display: "grid",
      gridTemplateColumns: "repeat(12, minmax(52px, 1fr))",
      gap: 0.75,
      width: "100%",
      // Horizontal scroll + padding so tile borders/shadows are not clipped
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      pt: 1,
      pb: 1.25,
      px: 0.25,
      boxSizing: "border-box",
    }),
    monthTile: (active, compact, disabled = false) => ({
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.25,
      minWidth: 0,
      width: "100%",
      minHeight: compact ? 48 : 52,
      height: compact ? 48 : 52,
      px: 0.25,
      py: 0.5,
      borderRadius: 2,
      textTransform: "none",
      overflow: "visible",
      borderColor: active ? "primary.main" : "divider",
      bgcolor: active
        ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.1)
        : "background.paper",
      color: active ? "primary.main" : disabled ? "text.disabled" : "text.primary",
      opacity: disabled ? 0.45 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: active
        ? `0 0 0 1px ${theme.palette.primary.main}`
        : `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
    }),
    monthTileIcon: (active) => ({
      fontSize: 18,
      opacity: active ? 1 : 0.72,
    }),
    monthTileLabel: {
      fontSize: "0.75rem",
      lineHeight: 1.1,
      textAlign: "center",
    },
    divider: { my: 1.25 },
  };
}
